import { i18n } from '@lingui/core'
import { t } from '@lingui/core/macro'

export function cmmsCurrency(
  record: Record<string, unknown>
): string | undefined {
  const value = record.currencycode
  if (typeof value !== 'string') return undefined
  const code = value.trim().toUpperCase()
  return /^[A-Z]{3}$/.test(code) ? code : undefined
}

export function formatCmmsMoney(value: string, currency?: string): string {
  const raw = value.trim()
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(raw)) return value
  const number = Number(raw)
  if (!Number.isFinite(number)) return value
  const [coefficient, exponent = '0'] = raw.toLowerCase().split('e')
  const precision = Math.min(
    20,
    Math.max(0, (coefficient.split('.')[1]?.length ?? 0) - Number(exponent))
  )
  const locale = i18n.locale || 'en'
  if (!currency) {
    const amount = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: Math.max(2, precision),
    }).format(number)
    return t`${amount} (currency not supplied)`
  }
  const standard = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  })
  const fractionDigits = standard.resolvedOptions().minimumFractionDigits ?? 2
  const symbol =
    standard.formatToParts(number).find((part) => part.type === 'currency')
      ?.value ?? currency
  const amount = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: /^[\u0020-\u00ff\u20ac]+$/.test(symbol)
      ? 'symbol'
      : 'code',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: Math.max(fractionDigits, precision),
  })
    .format(number)
    .replace(/[\u00a0\u202f]/g, ' ')
  return `${amount}${amount.includes(currency) ? '' : ` ${currency}`}`
}
